import type { SupergreenWebhookUpdate } from "./main.ts";

export const supergreenSignatureHeader = "x-supergreen-signature";

const encoder = new TextEncoder();

const toHex = (bytes: Uint8Array) =>
  Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");

const normalizeSignature = (signature: string) =>
  signature.startsWith("sha256=")
    ? signature.slice("sha256=".length)
    : signature;

const constantTimeEquals = (left: string, right: string) => {
  if (left.length !== right.length) return false;
  return Array.from(left).reduce(
    (acc, char, index) => acc | (char.charCodeAt(0) ^ right.charCodeAt(index)),
    0,
  ) === 0;
};

const importHmacKey = (secretToken: string) =>
  crypto.subtle.importKey(
    "raw",
    encoder.encode(secretToken),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

export const webhookPayloadString = (update: SupergreenWebhookUpdate) =>
  JSON.stringify(update);

export const signWebhookPayload = async (
  secretToken: string,
  payload: string,
) => {
  const bytes = new Uint8Array(
    await crypto.subtle.sign(
      "HMAC",
      await importHmacKey(secretToken),
      encoder.encode(payload),
    ),
  );
  return `sha256=${toHex(bytes)}`;
};

export const verifyWebhookSignature = async (
  {
    secretToken,
    payload,
    signature,
  }: {
    secretToken: string;
    payload: string;
    signature: string;
  },
) => {
  const expected = normalizeSignature(
    await signWebhookPayload(secretToken, payload),
  );
  const actual = normalizeSignature(signature);
  return constantTimeEquals(expected, actual);
};

export const parseAndVerifyWebhook = async (
  req: Request,
  secretToken: string,
): Promise<
  | { ok: true; payload: string; update: SupergreenWebhookUpdate }
  | { ok: false; error: string }
> => {
  const payload = await req.text();
  const signature = req.headers.get(supergreenSignatureHeader);
  if (!signature) return { ok: false, error: "Missing Supergreen signature" };
  if (!await verifyWebhookSignature({ secretToken, payload, signature })) {
    return { ok: false, error: "Invalid Supergreen signature" };
  }
  return {
    ok: true,
    payload,
    update: JSON.parse(payload) as SupergreenWebhookUpdate,
  };
};
